import React, { useMemo, useState } from 'react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend, 
  Scatter, 
  Line, 
  ReferenceArea, 
  Label 
} from 'recharts';
import { CLIMBING_STYLES } from '@utils/constants.js';
import { ASCENT_TYPES } from '@utils/ascentTypes.js';
import { customColors } from '@utils/gradeUtils.js';

const RegressionLabel = ({ xScale, yScale, equation, xDomain, yDomain }) => (
  <text
    x={xScale(xDomain[1]) - 5}
    y={yScale(yDomain[0]) - 5}
    fontSize={12}
    fill="#666"
    textAnchor="end"
  >
    {equation}
  </text>
);

const AscentProgressionChart = ({
  data,
  getGradeLabel,
  climbingStyle,
  gradeSystem
}) => {
  // State for custom tooltip
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  // Effect to handle mouse leaving the window
  React.useEffect(() => {
    const handleMouseLeave = (e) => {
      // Check if mouse is leaving the document
      if (e.clientY <= 0 || e.clientX <= 0 || 
          e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        setHoveredPoint(null);
      }
    };
    
    // Also handle when the entire document loses mouse
    const handleDocumentMouseLeave = () => {
      setHoveredPoint(null);
    };
    
    document.addEventListener('mousemove', handleMouseLeave);
    document.addEventListener('mouseleave', handleDocumentMouseLeave);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseLeave);
      document.removeEventListener('mouseleave', handleDocumentMouseLeave);
    };
  }, []);
  
  const memoizedGradeFormatter = useMemo(() => {
    return (value) => getGradeLabel(value, gradeSystem);
  }, [getGradeLabel, gradeSystem]);

  // Custom interactive dot component
  const InteractiveDot = (props) => {
    const { cx, cy, payload, fill, shape = 'circle' } = props;
    if (!cx || !cy) return null;
    
    const handleMouseEnter = (e) => {
      const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
      setHoveredPoint({
        x: rect.left + cx,
        y: rect.top + cy,
        data: payload
      });
    };
    
    const handleMouseLeave = () => {
      setHoveredPoint(null);
    };
    
    if (shape === 'square') {
      return (
        <rect
          x={cx - 4}
          y={cy - 4}
          width={8}
          height={8}
          fill={fill}
          style={{ cursor: 'pointer' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    } else if (shape === 'triangle') {
      return (
        <polygon
          points={`${cx},${cy - 4} ${cx - 4},${cy + 4} ${cx + 4},${cy + 4}`}
          fill={fill}
          style={{ cursor: 'pointer' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    }
    
    return (
      <circle 
        cx={cx} 
        cy={cy} 
        r={4} 
        fill={fill}
        style={{ cursor: 'pointer' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    );
  };
  
  // Custom failed attempt dot
  const FailedAttemptDot = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy) return null;
    
    return (
      <circle 
        cx={cx} 
        cy={cy} 
        r={2} 
        fill="#aaaaaa"
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => {
          const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
          setHoveredPoint({
            x: rect.left + cx,
            y: rect.top + cy,
            data: payload
          });
        }}
        onMouseLeave={() => setHoveredPoint(null)}
      />
    );
  };

  const chartData = useMemo(() => {
    if (!data?.length) return { 
      combinedData: [],
      xDomain: [0, 0],
      yDomain: [0, 0],
      equation: ''
    };

    const processDate = date => new Date(date).getTime();

    const processAscents = (ascents, climbingStyle) => {
      // Get all failed attempts first
      const failedAttempts = ascents
        .filter(ascent => 
          Object.values(ASCENT_TYPES.FAILED).includes(ascent["Ascent Type"]) &&
          !isNaN(climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks)
        )
        .map(ascent => ({
          date: processDate(ascent["Ascent Date"]),
          grade: Number(climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks),
          name: ascent["Route Name"],
          crag: ascent["Crag Name"],
          type: ascent["Ascent Type"]
        }));

      const baseAscents = ascents
        .filter(ascent => 
          (ascent["Ascent Type"] === "Flash" || ascent["Ascent Type"] === "Onsight") && 
          !isNaN(climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks)
        )
        .map(ascent => ({
          date: processDate(ascent["Ascent Date"]),
          grade: Number(climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks),
          name: ascent["Route Name"],
          crag: ascent["Crag Name"],
          type: ascent["Ascent Type"]
        }));

      if (climbingStyle === CLIMBING_STYLES.BOULDER) {
        const sends = ascents
          .filter(ascent => 
            (ascent["Ascent Type"] === "Send" || ascent["Ascent Type"] === "Repeat") && 
            !isNaN(ascent.vgrade)
          )
          .map(ascent => ({
            date: processDate(ascent["Ascent Date"]),
            grade: Number(ascent.vgrade),
            name: ascent["Route Name"],
            crag: ascent["Crag Name"],
            type: ascent["Ascent Type"]
          }));

        return [...failedAttempts, ...baseAscents, ...sends];
      } else {
        const redpoints = ascents
          .filter(ascent => 
            ascent["Ascent Type"] === "Red point" && 
            !isNaN(ascent.ewbanks)
          )
          .map(ascent => ({
            date: processDate(ascent["Ascent Date"]),
            grade: Number(ascent.ewbanks),
            name: ascent["Route Name"],
            crag: ascent["Crag Name"],
            type: "Red point"
          }));

        return [...failedAttempts, ...baseAscents, ...redpoints];
      }
    };

    const allAscents = processAscents(data, climbingStyle);

    // Calculate rolling CPG average
    const calculateRollingAverage = (ascents) => {
      if (ascents.length < 2) return [];
      
      // Filter successful ascents and sort by date
      const successfulAscents = ascents
        .filter(a => !Object.values(ASCENT_TYPES.FAILED).includes(a.type))
        .sort((a, b) => a.date - b.date);
      
      const minDate = Math.min(...successfulAscents.map(a => a.date));
      const maxDate = Math.max(...successfulAscents.map(a => a.date));
      const ascentsTimeSpan = maxDate - minDate;
      
      const adaptiveWindowSize = (100 * ascentsTimeSpan) / successfulAscents.length;

      // Helper function to calculate CPG for a set of ascents
      const calculateCPG = (windowPoints) => {
        // Group ascents by date to count unique climbing days
        const ascentsByDate = {};
        windowPoints.forEach(ascent => {
          const dateKey = new Date(ascent.date).toDateString();
          if (!ascentsByDate[dateKey]) {
            ascentsByDate[dateKey] = [];
          }
          ascentsByDate[dateKey].push(ascent);
        });

        const k = Object.keys(ascentsByDate).length; // Number of climbing days
        if (k === 0) return null;

        // Calculate sum of 2^grade for all ascents
        const sumPowerGrades = windowPoints.reduce((sum, ascent) => {
          return sum + Math.pow(2, ascent.grade);
        }, 0);

        // Calculate CPG using the formula: log2(sum(2^grade_i) / k)
        return Math.log2(sumPowerGrades / k);
      };
      
      return successfulAscents.map(current => {
        const windowStart = current.date - adaptiveWindowSize / 2;
        const windowEnd = current.date + adaptiveWindowSize / 2;
        
        const windowPoints = successfulAscents.filter(
          point => point.date >= windowStart && point.date <= windowEnd
        );
        
        if (windowPoints.length >= 3) {
          const cpg = calculateCPG(windowPoints);
          return cpg !== null ? {
            date: current.date,
            rollingAvg: cpg
          } : null;
        }
        return null;
      }).filter(Boolean);
    };
    
    if (allAscents.length > 1) {
      // Filter out attempts for regression calculation
      const successfulAscents = allAscents.filter(a => !Object.values(ASCENT_TYPES.FAILED).includes(a.type));
      
      const dates = successfulAscents.map(a => a.date);
      const grades = successfulAscents.map(a => a.grade);
      
      // Get ALL grades including failed attempts for proper Y-axis bounds
      const allGrades = allAscents.map(a => a.grade);
      
      const minDate = Math.min(...dates);
      const maxDate = Math.max(...dates);
      const minGrade = Math.min(...allGrades);
      const maxGrade = Math.max(...allGrades);
      
      // Calculate regression coefficients
      const n = successfulAscents.length;
      const sumX = successfulAscents.reduce((sum, a) => sum + a.date, 0);
      const sumY = successfulAscents.reduce((sum, a) => sum + a.grade, 0);
      const sumXY = successfulAscents.reduce((sum, a) => sum + (a.date * a.grade), 0);
      const sumX2 = successfulAscents.reduce((sum, a) => sum + (a.date * a.date), 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;

      const msPerYear = 365 * 24 * 60 * 60 * 1000;
      const gradeChangePerYear = slope * msPerYear;

      const equation = `Grade progression: ${gradeChangePerYear > 0 ? '+' : ''}${gradeChangePerYear.toFixed(2)} grades/year`;

      // Calculate rolling averages and sort them
      const rawRollingAverages = calculateRollingAverage(allAscents);
      const sortedRollingAverages = rawRollingAverages.sort((a, b) => a.date - b.date);
      
      // Add gaps between disconnected periods
      const rollingAveragesWithGaps = [];
      for (let i = 0; i < sortedRollingAverages.length; i++) {
        rollingAveragesWithGaps.push(sortedRollingAverages[i]);
        
        // If there's a next point and it's too far away, add a null point
        if (i < sortedRollingAverages.length - 1) {
          const timeDiff = sortedRollingAverages[i + 1].date - sortedRollingAverages[i].date;
          const avgTimeBetweenPoints = (maxDate - minDate) / successfulAscents.length;
          if (timeDiff > avgTimeBetweenPoints * 2) { // Gap threshold
            rollingAveragesWithGaps.push({
              date: sortedRollingAverages[i].date + timeDiff / 2,
              rollingAvg: null
            });
          }
        }
      }
      
      const combinedData = [
        ...allAscents,
        {
          date: minDate,
          trendline: slope * minDate + intercept,
          type: "trend"
        },
        {
          date: maxDate,
          trendline: slope * maxDate + intercept,
          type: "trend"
        },
        ...rollingAveragesWithGaps
     ];

      return {
        combinedData,
        xDomain: [minDate, maxDate],
        yDomain: [minGrade - 0.5, maxGrade + 0.5],
        equation
      };
    }

    return {
      combinedData: allAscents,
      xDomain: [
        Math.min(...allAscents.map(a => a.date)),
        Math.max(...allAscents.map(a => a.date))
      ],
      yDomain: [
        Math.min(...allAscents.map(a => a.grade)) - 0.5,
        Math.max(...allAscents.map(a => a.grade)) + 0.5
      ],
      equation: ''
    };
  }, [data, climbingStyle]);

  const leftMargin = (gradeSystem === 'ewbank' ? -12 : 12);

  const yearBoundaries = useMemo(() => {
    if (!chartData.combinedData?.length) return [];
    
    const minDate = chartData.xDomain[0];
    const maxDate = chartData.xDomain[1];
    const startYear = new Date(minDate).getFullYear();
    const endYear = new Date(maxDate).getFullYear();
    
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => {
      const year = startYear + index;
      const yearStart = new Date(year, 0, 1).getTime();
      const yearEnd = new Date(year + 1, 0, 1).getTime();
      
      return {
        start: Math.max(yearStart, minDate),  // Clamp to data range
        end: Math.min(yearEnd, maxDate),      // Clamp to data range
        isEven: index % 2 === 0
      };
    });
  }, [chartData.combinedData, chartData.xDomain]);
  
  // Calculate ticks and format based on date range
  const { xAxisTicks, tickFormatter } = useMemo(() => {
    if (!yearBoundaries.length) return { xAxisTicks: [], tickFormatter: () => '' };

    const startYear = new Date(chartData.xDomain[0]).getFullYear();
    const endYear = new Date(chartData.xDomain[1]).getFullYear();
    
    // If data spans a single year, show monthly ticks
    if (startYear === endYear) {
      const ticks = [];
      for (let month = 0; month < 12; month++) {
        ticks.push(new Date(startYear, month, 1).getTime());
      }
      return {
        xAxisTicks: ticks,
        tickFormatter: (timestamp) => 
          new Date(timestamp).toLocaleDateString('en-US', { 
            month: 'short'
          })
      };
    }
    
    // Otherwise, show yearly ticks
    return {
      xAxisTicks: yearBoundaries.map(boundary => boundary.start),
      tickFormatter: (timestamp) => 
        new Date(timestamp).toLocaleDateString('en-US', { 
          year: 'numeric'
        })
    };
  }, [yearBoundaries, chartData.xDomain]);

  return (
    <div 
      className="relative w-full h-full bg-white"
      onMouseLeave={() => setHoveredPoint(null)}
    >
      {hoveredPoint && (
        <div 
          style={{
            position: 'fixed',
            left: hoveredPoint.x + 10,
            top: hoveredPoint.y - 10,
            pointerEvents: 'none',
            zIndex: 1000
          }}
          className="bg-white p-2 border border-gray-200 shadow-lg rounded"
        >
          <p className="font-medium">{hoveredPoint.data.name}</p>
          <p className="text-sm text-gray-600">{hoveredPoint.data.crag}</p>
          <p className="text-sm">
            {new Date(hoveredPoint.data.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </p>
          <p className="text-sm font-medium">{hoveredPoint.data.type}</p>
          <p className="text-sm">{memoizedGradeFormatter(hoveredPoint.data.grade)}</p>
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          margin={{ top: 0, right: 5, left: leftMargin, bottom: 22 }}
          data={chartData.combinedData}
        >
          {/* Add year stripes before the CartesianGrid */}
          {yearBoundaries.map((boundary, index) => (
            boundary.isEven ? (
              <ReferenceArea
                key={boundary.start}
                x1={boundary.start}
                x2={boundary.end}
                fill="#f3f4f6"
                fillOpacity={1}
              />
            ) : null
          ))}

          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="date" 
            type="number" 
            domain={chartData.xDomain} 
            ticks={xAxisTicks}
            tickFormatter={tickFormatter}
            tick={{ fontSize: 12 }}
          >
            <Label
              value={chartData.equation}
              position="insideBottomRight"
              dy={20}
              style={{
                textAnchor: "end",
                fill: "#666",
                fontSize: "12px",
                fontWeight: "bold"
              }}
            />
          </XAxis>
          <YAxis 
            domain={chartData.yDomain}
            tickFormatter={memoizedGradeFormatter}
            ticks={Array.from(
              { length: Math.floor(chartData.yDomain[1]) - Math.ceil(chartData.yDomain[0]) + 1 },
              (_, i) => Math.ceil(chartData.yDomain[0]) + i
            )}
            label={{
              value: 'Grade',
              angle: -90,
              position: 'middle',
              dx: gradeSystem === 'ewbank' ? -10 : -35
            }}
            interval={0}
            tick={{ fontSize: 12 }}
          />
          
          {/* Draw all failed attempts first */}
          {Object.values(ASCENT_TYPES.FAILED).map(attemptType => {
            const attemptData = chartData.combinedData.filter(d => d.type === attemptType);
            if (attemptData.length === 0) return null;
            
            return (
              <Scatter
                key={attemptType}
                name={attemptType}
                dataKey="grade"
                data={attemptData}
                shape={<FailedAttemptDot />}
              />
            );
          })}
                    
          {climbingStyle === CLIMBING_STYLES.BOULDER ? (
            <>
              {chartData.combinedData.some(d => d.type === "Send") && (
                <Scatter 
                  name="Send" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Send")}
                  fill="#3b82f6"
                  shape={(props) => <InteractiveDot {...props} shape="circle" />}
                />
              )}
              {chartData.combinedData.some(d => d.type === "Repeat") && (
                <Scatter 
                  name="Repeat" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Repeat")}
                  fill="#8b5cf6"
                  shape={(props) => <InteractiveDot {...props} shape="circle" />}
                />
              )}
              {chartData.combinedData.some(d => d.type === "Flash") && (
                <Scatter 
                  name="Flash" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Flash")}
                  fill="#f98326"
                  shape={(props) => <InteractiveDot {...props} shape="circle" />}
                />
              )}
              {chartData.combinedData.some(d => d.type === "Onsight") && (
                <Scatter 
                  name="Onsight" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Onsight")}
                  fill="#22c55e"
                  shape={(props) => <InteractiveDot {...props} shape="triangle" />}
                />
              )}
            </>
          ) : (
            <>
              {chartData.combinedData.some(d => d.type === "Red point") && (
                <Scatter 
                  name="Red point" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Red point")}
                  fill="#ef4444"
                  shape={(props) => <InteractiveDot {...props} shape="circle" />}
                />
              )}
              {chartData.combinedData.some(d => d.type === "Flash") && (
                <Scatter 
                  name="Flash" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Flash")}
                  fill="#f97316"
                  shape={(props) => <InteractiveDot {...props} shape="circle" />}
                />
              )}
              {chartData.combinedData.some(d => d.type === "Onsight") && (
                <Scatter 
                  name="Onsight" 
                  dataKey="grade"
                  data={chartData.combinedData.filter(d => d.type === "Onsight")}
                  fill="#22c55e"
                  shape={(props) => <InteractiveDot {...props} shape="circle" />}
                />
              )}
            </>
          )}

          <Line
            name="Grade Trend"
            dataKey="trendline"
            stroke="#6b7280"
            strokeWidth={1.5}
            strokeDasharray="5 3"
            dot={false}
            connectNulls={true}
          />
          
          <Line
            name="Rolling Average"
            dataKey="rollingAvg"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
            connectNulls={true}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AscentProgressionChart;